/**
 * PublicScenarioStage: the /demo-business/scenarios/:scenarioId page.
 *
 * Thin public wrapper around the existing DemoStage engine.  Passes
 * `audience="public"` so the shared engine hides:
 *   - the promoter Sales Notes ("What to explain")
 *   - Manager View toggle
 *   - Log Visit button
 *
 * ...and replaces them with a short business-owner explainer above the
 * fold and a friendly "Try ConfirmPH for my business" CTA below.
 *
 * The scenario engine, sandbox rules and Phone/Desktop toggle are
 * completely unchanged.
 */

import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import DemoStage from "../promoter/demos/DemoStage";
import { OWNER_EXPLAINERS } from "./ownerExplainers";

/**
 * `?embed=1` is set only by the business-homepage walkthrough
 * (components/BusinessProductWalkthrough.jsx) when it loads this page in
 * an iframe: it strips this page's own header/back-link and tells
 * DemoStage to hide its own chrome too, leaving just the interactive
 * phone/desktop content. The standalone public catalog (no query param)
 * and the promoter/company-dashboard demo tool (a completely different
 * route, /promoter/demos) are both entirely unaffected.
 */
export default function PublicScenarioStage() {
  const [searchParams] = useSearchParams();
  const embedded = searchParams.get("embed") === "1";

  if (embedded) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <DemoStage audience="public" ownerExplainers={OWNER_EXPLAINERS} embedded />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <PublicHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Link to="/demo-business/scenarios" data-testid="public-scenario-back"
              className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft className="w-4 h-4" /> All scenarios
        </Link>
        <div className="mt-4">
          <DemoStage audience="public" ownerExplainers={OWNER_EXPLAINERS} />
        </div>
      </main>
    </div>
  );
}

function PublicHeader() {
  return (
    <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
        <Link to="/" className="flex items-center gap-2" data-testid="public-scenario-brand">
          <img src="/brand/confirmph-logo.png" alt="ConfirmPH" className="h-7 w-auto"
                onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <span className="font-display font-semibold text-slate-900">ConfirmPH</span>
        </Link>
        <span className="text-[10px] uppercase tracking-[0.2em] text-rose-700 font-bold border border-rose-200 bg-rose-50 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
          <ShieldCheck className="w-3 h-3" /> Demo
        </span>
        <div className="flex-1" />
        <Link to="/demo-business/scenarios" data-testid="public-scenario-all"
              className="text-xs text-slate-600 hover:text-slate-900">
          All scenarios
        </Link>
        <Link to="/register?ref=demo-scenarios" data-testid="public-scenario-signup"
              className="text-xs px-3 py-1.5 rounded-full bg-[#1B4332] text-white font-medium">
          Start free
        </Link>
      </div>
    </header>
  );
}
